import React, { useEffect, useState } from "react";
import { getAllDocsSnap } from "../Firebase/Firebase.utils";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import {
  HiDocumentReport,
  HiX,
  HiChartBar,
  HiUserGroup,
  HiOutlineCurrencyDollar,
  HiOutlineOfficeBuilding,
} from "react-icons/hi";
import { BsFilter, BsCircleFill } from "react-icons/bs";
import { RiPercentFill } from "react-icons/ri";

export default function SummaryReport({ setViewMode, data }) {
  const [agents, setAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const statusPercentages = ["10%", "30%", "50%", "90%", "100%"];
  const [selectedStatusArray, setSelectedStatusArray] = useState(
    statusPercentages || []
  );
  const [activeTab, setActiveTab] = useState("overview");

  // Color palette from design.md
  const PRIMARY_COLOR = "#002368";
  const ACCENT_COLOR = "#ff5003";
  const CHART_COLORS = [
    PRIMARY_COLOR,
    "#1e40af",
    "#3b82f6",
    "#93c5fd",
    ACCENT_COLOR,
  ];

  useEffect(() => {
    const fetchCommercialLeasingAgents = async () => {
      await getAllDocsSnap("users", (agents) => {
        const commercialLeasingAgents = agents.filter(
          (agent) =>
            agent.department === "Commercial Leasing" &&
            agent.roles.includes("agent")
        );
        // sort agents by name
        const sortedAgents = commercialLeasingAgents.sort((a, b) =>
          a.displayName.localeCompare(b.displayName)
        );

        setAgents(sortedAgents);
        setIsLoading(false);
      });
    };
    fetchCommercialLeasingAgents();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const accounts = data;

  const activeAccounts = accounts.filter(
    (account) => account.status === "active"
  );

  const inactiveAccounts = accounts.filter(
    (account) => account.status === "inactive"
  );

  const accountsWithoutStatus = accounts.filter(
    (act) => act.status === undefined || act.status === null
  ).length;

  const tenPercenters = activeAccounts.filter(
    (item) => item.pipeline_status === "10%"
  );

  const thirtyPercenters = activeAccounts.filter(
    (item) => item.pipeline_status === "30%"
  );
  const fiftyPercenters = activeAccounts.filter(
    (item) => item.pipeline_status === "50%"
  );
  const ninetyPercenters = activeAccounts.filter(
    (item) => item.pipeline_status === "90%"
  );
  const hundredPercenters = activeAccounts.filter(
    (item) => item.pipeline_status === "100%"
  );

  const handleSum = (acc, curr) => {
    if (!curr.potentialFee) {
      return acc + 0;
    } else {
      const value = parseFloat(curr.potentialFee.replace(/,/g, ""));
      return acc + value;
    }
  };

  const tenPercentersFee = tenPercenters.reduce(handleSum, 0);
  const thirtyPercentersFee = thirtyPercenters.reduce(handleSum, 0);
  const fiftyPercentersFee = fiftyPercenters.reduce(handleSum, 0);
  const ninetyPercentersFee = ninetyPercenters.reduce(handleSum, 0);
  const hundredPercentersFee = hundredPercenters.reduce(handleSum, 0);
  const totalFee = accounts.reduce(handleSum, 0);

  const handleStatus = (status) => {
    if (selectedStatusArray.includes(status)) {
      setSelectedStatusArray(
        selectedStatusArray.filter((item) => item !== status)
      );
    } else {
      setSelectedStatusArray([...selectedStatusArray, status]);
    }
  };

  const filteredActiveAccounts = activeAccounts.filter((account) =>
    selectedStatusArray.includes(account.pipeline_status)
  );

  const handleAllStatus = () => {
    setSelectedStatusArray(statusPercentages);
  };

  // Data for charts
  const accountStatusData = [
    { name: "Active", value: activeAccounts.length },
    { name: "Inactive", value: inactiveAccounts.length },
    { name: "No Status", value: accountsWithoutStatus },
  ];

  const pipelineStatusData = [
    { name: "10%", value: tenPercenters.length, fee: tenPercentersFee },
    { name: "30%", value: thirtyPercenters.length, fee: thirtyPercentersFee },
    { name: "50%", value: fiftyPercenters.length, fee: fiftyPercentersFee },
    { name: "90%", value: ninetyPercenters.length, fee: ninetyPercentersFee },
    {
      name: "100%",
      value: hundredPercenters.length,
      fee: hundredPercentersFee,
    },
  ];

  const agentPerformanceData = agents
    .map((agent) => ({
      name: agent.displayName.split(" ")[0],
      accounts: filteredActiveAccounts.filter(
        (account) => account.team_lead === agent.email
      ).length,
    }))
    .sort((a, b) => b.accounts - a.accounts);

  return (
    <div className="relative bg-white text-gray-700 p-6 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <header className="flex justify-between items-center mb-8">
          <div className="flex items-center">
            <HiDocumentReport size={24} className="text-[#002368] mr-2" />
            <h1 className="text-2xl font-bold text-[#002368]">
              Summary Report
            </h1>
            <span className="ml-2 text-sm bg-[#002368] text-white px-2 py-1 rounded-md">
              Commercial Leasing Team
            </span>
          </div>
          <button
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            onClick={() => setViewMode(false)}
          >
            <HiX size={20} className="text-gray-500" />
          </button>
        </header>

        <div className="mb-6">
          <div className="flex border-b">
            <button
              className={`px-4 py-2 flex items-center ${
                activeTab === "overview"
                  ? "border-b-2 border-[#ff5003] text-[#002368] font-medium"
                  : "text-gray-500"
              }`}
              onClick={() => setActiveTab("overview")}
            >
              <HiChartBar className="mr-2" /> Overview
            </button>
            <button
              className={`px-4 py-2 flex items-center ${
                activeTab === "agents"
                  ? "border-b-2 border-[#ff5003] text-[#002368] font-medium"
                  : "text-gray-500"
              }`}
              onClick={() => setActiveTab("agents")}
            >
              <HiUserGroup className="mr-2" /> Agent Performance
            </button>
            <button
              className={`px-4 py-2 flex items-center ${
                activeTab === "pipeline"
                  ? "border-b-2 border-[#ff5003] text-[#002368] font-medium"
                  : "text-gray-500"
              }`}
              onClick={() => setActiveTab("pipeline")}
            >
              <RiPercentFill className="mr-2" /> Pipeline Status
            </button>
          </div>
        </div>

        {activeTab === "overview" && (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-sm border p-5">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-[#002368]">
                  Account Overview
                </h2>
                <HiOutlineOfficeBuilding className="text-[#ff5003]" size={20} />
              </div>
              <div className="grid grid-cols-2 gap-5">
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={accountStatusData}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) =>
                          `${name}: ${(percent * 100).toFixed(0)}%`
                        }
                      >
                        {accountStatusData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={CHART_COLORS[index % CHART_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => [`${value} accounts`, "Count"]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-500">Active Accounts</div>
                    <div className="text-2xl font-semibold text-[#002368]">
                      {activeAccounts.length}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-500">
                      Inactive Accounts
                    </div>
                    <div className="text-2xl font-semibold text-[#002368]">
                      {inactiveAccounts.length}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-500">Total Accounts</div>
                    <div className="text-2xl font-semibold text-[#002368]">
                      {accounts.length}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border p-5">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-[#002368]">
                  Fee Summary
                </h2>
                <HiOutlineCurrencyDollar className="text-[#ff5003]" size={22} />
              </div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={pipelineStatusData}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip
                      formatter={(value) => [
                        value.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }),
                        "Fee Amount",
                      ]}
                    />
                    <Bar dataKey="fee" fill={PRIMARY_COLOR} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-500">Total Potential Fee</div>
                <div className="text-2xl font-semibold text-[#002368]">
                  {totalFee.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "pipeline" && (
          <div className="bg-white rounded-lg shadow-sm border p-5">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-[#002368]">
                Pipeline Status Breakdown
              </h2>
              <div className="flex items-center">
                <BsFilter className="text-gray-400 mr-2" size={20} />
                <span className="text-sm text-gray-500">Filter by status</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              <button
                onClick={handleAllStatus}
                className={`px-3 py-1 rounded-full text-xs ${
                  selectedStatusArray.length === statusPercentages.length
                    ? "bg-[#002368] text-white"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                All
              </button>
              {statusPercentages.map((status, index) => (
                <button
                  key={index}
                  onClick={() => handleStatus(status)}
                  className={`px-3 py-1 rounded-full text-xs flex items-center ${
                    selectedStatusArray.includes(status)
                      ? "bg-[#ff5003] text-white"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  <RiPercentFill className="mr-1" /> {status}
                </button>
              ))}
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Count
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Potential Fee
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pipelineStatusData.map((item, index) => (
                    <tr
                      key={index}
                      className={
                        selectedStatusArray.includes(item.name)
                          ? ""
                          : "opacity-50"
                      }
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <BsCircleFill
                            className="mr-2"
                            size={8}
                            style={{
                              color: CHART_COLORS[index % CHART_COLORS.length],
                            }}
                          />
                          <span className="font-medium">{item.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {item.value}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {item.fee.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-semibold">
                    <td className="px-6 py-4 whitespace-nowrap">Total</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {activeAccounts.length}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {totalFee.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap">
                      Total of 30% to 90%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {thirtyPercenters.length +
                        fiftyPercenters.length +
                        ninetyPercenters.length}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(
                        thirtyPercentersFee +
                        fiftyPercentersFee +
                        ninetyPercentersFee
                      ).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "agents" && (
          <div className="bg-white rounded-lg shadow-sm border p-5">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-[#002368]">
                Agent Performance
              </h2>
              <div className="flex items-center">
                <span className="text-sm bg-[#002368] text-white px-2 py-1 rounded-md mr-2">
                  {filteredActiveAccounts.length} Active Accounts
                </span>
                <BsFilter className="text-gray-400 mr-2" size={20} />
                <span className="text-sm text-gray-500">Filter by status</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              <button
                onClick={handleAllStatus}
                className={`px-3 py-1 rounded-full text-xs ${
                  selectedStatusArray.length === statusPercentages.length
                    ? "bg-[#002368] text-white"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                All
              </button>
              {statusPercentages.map((status, index) => (
                <button
                  key={index}
                  onClick={() => handleStatus(status)}
                  className={`px-3 py-1 rounded-full text-xs flex items-center ${
                    selectedStatusArray.includes(status)
                      ? "bg-[#ff5003] text-white"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  <RiPercentFill className="mr-1" /> {status}
                </button>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={agentPerformanceData.slice(0, 10)}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 50, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" />
                    <Tooltip />
                    <Bar dataKey="accounts" fill={ACCENT_COLOR} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="space-y-4">
                <div className="overflow-auto max-h-80">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Agent
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Accounts
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {agents.map((agent, index) => (
                        <tr key={index}>
                          <td className="px-6 py-2 whitespace-nowrap">
                            {agent.displayName}
                          </td>
                          <td className="px-6 py-2 whitespace-nowrap text-right">
                            {
                              filteredActiveAccounts.filter(
                                (account) => account.team_lead === agent.email
                              ).length
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-500">Average per Agent</div>
                  <div className="text-2xl font-semibold text-[#002368]">
                    {(filteredActiveAccounts.length / agents.length).toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
